import { Dict } from "mixpanel-browser";
import { IAnalyticsProvider } from "../../core/ports/driven/analyticsProvider";
import { reportAppError } from "../../../sentry";
import { localStorageProvider } from "../../../AppController";
const env = import.meta.env;
import { apiOrigin } from "../../../config";

export type IAnalyticsController = ReturnType<
  typeof AnalyticsControllerFactory
>;

export const AnalyticsControllerFactory = ({
  analyticsProvider,
  circadiaEngineProvider,
}: {
  analyticsProvider: IAnalyticsProvider;
  circadiaEngineProvider: ICircadiaEngineProvider;
}) => {
  try {
    analyticsProvider.init(env.VITE_ANALYTICS_KEY, {
      // api_host: "https://api.mixpanel.com",
      api_host: `${apiOrigin}/patient-centric-app-v2/mixpanel`,
      persistence: "localStorage",
      ignore_dnt: true,
      api_transport: "sendBeacon",
    });
  } catch (error) {
    reportAppError(error as Error, {
      component: "analyticsController",
      action: "init",
      additionalData: {
        error: error as string,
        failedFor: localStorageProvider.get("userEmail") || "unknown",
      },
    });
    console.error("[Analytics] Failed to initialize analytics:", error);
  }

  const resetEvent = () => {
    analyticsProvider.reset();
  };
  const logEvent = (
    eventName: string,
    eventProperties?: Dict,
    identity?: string
  ) => {
    if (identity) {
      analyticsProvider.identify(identity);
    }
    const eventProp: Dict = {
      ...eventProperties,
      appVersion: APP_VERSION,
    };

    if (circadiaEngineProvider.isWindowCircadia()) {
      eventProp.intfv = circadiaEngineProvider.intfv;
      eventProp.plat = circadiaEngineProvider.plat;
    }

    analyticsProvider.track(eventName, eventProp);
    // const intercomEventProp = { ...eventProp }
    // delete intercomEventProp.token
    // delete intercomEventProp.selectedFacilityId
    // delete intercomEventProp.cUserId
    // webIntercomProvider.trackEvent(eventName, intercomEventProp);
  };

  const timeEvent = (eventName: string) => {
    analyticsProvider.timeEvent(eventName);
  };

  const setPeople = (obj: {
    name: string;
    role: string;
    email: string;
    userID: string;
  }) => {
    analyticsProvider.setPeople(obj);
  };

  const identify = (identity: string) => {
    analyticsProvider.identify(identity);
  };

  // Enhanced error logging that reports to both Mixpanel and Sentry
  const logApiError = (
    error: Error,
    context: {
      method: string;
      url?: string;
      status?: number;
      apiName?: string;
      requestBody?: unknown;
      responseBody?: unknown;
      duration?: number;
    }
  ) => {
    // Log to Mixpanel (existing behavior)
    logEvent("api-failure", {
      apiName: context.apiName || "unknown",
      method: context.method,
      status: context.status,
      api_url: context.url,
      error_message: error.message,
      error_name: error.name,
    });
  };

  const logAppError = (
    error: Error,
    context: {
      component?: string;
      action?: string;
      userId?: string;
      additionalData?: Record<string, unknown>;
    } = {}
  ) => {
    // Log to Mixpanel
    logEvent("app-error", {
      component: context.component || "unknown",
      action: context.action || "unknown",
      error_message: error.message,
      error_name: error.name,
      user_id: context.userId,
      ...context.additionalData,
    });
  };

  return {
    resetEvent,
    logEvent,
    timeEvent,
    setPeople,
    identify,
    logApiError,
    logAppError,
  };
};
